import { useEffect, useRef, useState } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";

import { SecondScreenHelper } from "@/Common/Helpers/SecondScreenHelper";
import { SecondScreenOperation } from "@/Common/Models/SecondScreen/Enum/SecondScreenOperation";
import { SecondScreenNoteData } from "@/Common/Models/SecondScreenNoteData";
import { ModulesProvider } from "@/Common/Modularity/Components/ModulesProvider";
import { SecondScreenPage } from "../EntryPoints/SecondScreenApp/SecondScreenPage";

import "./SecondScreenApp.css";

import MedfarLogo from "@/DesignSystem/Atoms/Icons/Types/MedfarMedfarLogo/medfar-medfar-logo.svg";

function SecondScreenApp(): JSX.Element {
    const [isParentWindowOpen, setIsParentWindowOpen] = useState(true);
    const [openNoPatientScreen, setOpenNoPatientScreen] = useState(false);
    const [restrictedAccess, setRestrictedAccess] = useState(false);

    const parentValidationTimer = useRef(null);
    const patientStoreEventListenerId = useRef("");
    const nextPatientId = useRef(null);

    function checkParentWindowOpen(): void {
        if (!window.opener || window.opener.closed || !window.opener.sessionStore?.isAuthenticated()) {
            setIsParentWindowOpen(false);
            if (parentValidationTimer) {
                clearInterval(parentValidationTimer.current);
                parentValidationTimer.current = null;
            }
        }
    }

    function isAccessToPatientFileDenied(): void {
        setRestrictedAccess(patientStore.isPatientFileDeniedToCurrentUser(nextPatientId.current));
    }

    useEffect(() => {
        const initialData = window["initialData"] as SecondScreenNoteData;
        if (!initialData) {
            window.location.href = window.location.origin;
            return;
        }
        if (!IsNullOrUndefined(initialData.patientId)) {
            window.patientStore.setActivePatientUI(
                initialData.patientId,
                false,
                () => {
                    patientStore.ActionsMedicalSummary.LoadPatientCumulativeProfile(
                        initialData.patientId,
                        null,
                        true,
                        true,
                        "SecondScreen_InitialActivePatient",
                        () => {
                            if (initialData.encounterFilterMyNotes) {
                                SecondScreenHelper.executeOperation(SecondScreenOperation.FilterEncounterMyNotes, { value: initialData.encounterFilterMyNotes });
                            }
                            else if (initialData.encounterFilterUnapprovedNotes) {
                                SecondScreenHelper.executeOperation(SecondScreenOperation.FilterEncounterUnapprovedNotes, { value: initialData.encounterFilterUnapprovedNotes });
                            }
                            else if (initialData.encounterFilterClinicVisitNotes) {
                                SecondScreenHelper.executeOperation(SecondScreenOperation.FilterEncounterClinicVisitNotes, { value: initialData.encounterFilterClinicVisitNotes });
                            }
                        }
                    );
                },
                false,
                false
            );
        }

        window.addEventListener(window["noteServiceName"], (data: CustomEvent<SecondScreenNoteData>) => {
            const { patientId, encounterId, isEncounterDeleted } = data.detail;

            setOpenNoPatientScreen(!patientId);

            if (!patientId) return;

            const isPatientFileProtected = patientStore.isPatientFileDeniedToCurrentUser(patientId);
            setRestrictedAccess(isPatientFileProtected);
            if (isPatientFileProtected) return;

            if (encounterId) {
                patientStore.ActionsMedicalSummary.LoadPatientCumulativeProfile(
                    patientId,
                    null,
                    true,
                    true,
                    "SecondScreen_RefreshAfterNoteEvent",
                    async () => await patientStore.ActionsMedicalNote.LoadEncountersCache([encounterId], patientId),
                    true
                );
            }
            else if (isEncounterDeleted) {
                patientStore.ActionsMedicalSummary.LoadPatientCumulativeProfile(
                    patientId,
                    null,
                    true,
                    true,
                    "SecondScreen_RefreshAfterEncounterDeleted",
                    () => {
                        patientStore.cleanEncountersCache();
                    },
                    true
                );
            }
            else if (patientId !== patientStore.getCurrentPatientContactId()) {
                nextPatientId.current = patientId;
                window.patientStore.setActivePatientUI(patientId, false, () => {
                    patientStore.ActionsMedicalSummary.LoadPatientCumulativeProfile(
                        patientId,
                        null,
                        true,
                        true,
                        "SecondScreen_RefreshAfterPatientChanged"
                    );
                }, false, true);
                window.patientStore.EmitChangeToListeners();
            }
        }, false);

        window.addEventListener(window["encounterUpdateServiceName"], (data: CustomEvent<{ operation: SecondScreenOperation, payload: any }>) => {
            const { operation, payload } = data.detail;
            SecondScreenHelper.executeOperation(operation, payload);
        }, false);

        if (cacheStore.ClinicSettings.EnablePatientChartAllowlist) {
            nextPatientId.current = initialData.patientId;
            patientStoreEventListenerId.current = patientStore.AddChangeListener(() => isAccessToPatientFileDenied(), this, "second-patient-store");
        }

        parentValidationTimer.current = window.setInterval(() => checkParentWindowOpen(), 500);

        return () => {
            if (parentValidationTimer) {
                clearInterval(parentValidationTimer.current);
                parentValidationTimer.current = null;
            }

            if (cacheStore.ClinicSettings.EnablePatientChartAllowlist)
                patientStore.RemoveChangeListener(patientStoreEventListenerId.current);
        };
    }, []);

    if (window.sessionStore.isAuthenticated() && isParentWindowOpen && !openNoPatientScreen && !restrictedAccess) {
        return (
            <ModulesProvider>
                <Router basename={window.rootPath}>
                    <Routes>
                        <Route
                            path="2ndScreen"
                            element={(
                                <SecondScreenPage />
                            )}
                        />
                    </Routes>
                </Router>
            </ModulesProvider>
        );
    }
    else if (openNoPatientScreen) {
        return getSecondScreenMessageScreen(cacheStore.GetString("NoActivePatientFileLabel"), cacheStore.GetString("SelectNewPatientLabel"));
    }
    else if (restrictedAccess) {
        return getSecondScreenMessageScreen(cacheStore.GetString("Allowlist_RestrictedAccessLabel"), cacheStore.GetString("Allowlist_PatientFileAccessDeniedMessage"));
    }
    else {
        try {
            window.close();
        }
        catch { }

        // Fallback in case the browser does not allow scripts to close windows.
        const mainPage = `${location.protocol}//${location.host}`;
        return getSecondScreenMessageScreen(cacheStore.GetString("PrimaryScreenConnectionLost"), cacheStore.GetString("LogBackIntoMyle"), mainPage);
    }
}

const getSecondScreenMessageScreen = (title: string, message: string, link?: string): JSX.Element => (
    <div className="second-screen-message-container">
        <div className="second-screen-logo-container">
            <MedfarLogo className="myle-logo" />
        </div>
        <div>
            <span className="second-screen-message-container-title">{title}</span>
            <span className="second-screen-message-container-message">{message} {!IsNullOrUndefined(link) && <a href={link}>{link}</a>}</span>
        </div>
    </div>
);

export { SecondScreenApp };
